import { trace } from '@opentelemetry/api'

/**
 * A stable session-level trace ID generated once when the app loads.
 *
 * In a frontend app, many log-worthy events (catch blocks, React error
 * boundaries, useEffect, Firebase init, etc.) occur outside of any active
 * OTel span. Without a fallback, those logs carry no trace ID making it
 * impossible to correlate them in Grafana Tempo.
 *
 * This ID is NOT an OTel trace — it is a session correlation key that groups
 * all logs from a single page load together when no real span is active.
 */
export const SESSION_TRACE_ID = crypto.randomUUID().replace(/-/g, '')

/**
 * Returns the trace_id and span_id from the currently active OTel span.
 * Falls back to a session-level trace_id when no span is active so that
 * every log emission is always correlatable.
 *
 * Priority:
 *   1. Active OTel span  → real trace_id + span_id  (best: ties to backend trace)
 *   2. No active span    → session trace_id only     (good: groups all session logs)
 */
export function getActiveTraceContext():
    | { trace_id: string; span_id: string }
    | { trace_id: string } {
    const span = trace.getActiveSpan()
    if (span) {
        const ctx = span.spanContext()
        if (ctx.traceId && ctx.spanId) {
            return { trace_id: ctx.traceId, span_id: ctx.spanId }
        }
    }
    return { trace_id: SESSION_TRACE_ID }
}
